/**
 * App-wide display dates: DD-MON-YYYY (e.g. 07-AUG-2026).
 * Month is always English uppercase abbreviation.
 */

const MONTHS = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
];

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function toValidDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      const y = Number(dateOnly[1]);
      const m = Number(dateOnly[2]);
      const day = Number(dateOnly[3]);
      const local = new Date(y, m - 1, day);
      return Number.isNaN(local.getTime()) ? null : local;
    }
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** Date only → 07-AUG-2026 */
export function formatAppDate(value, empty = '—') {
  const d = toValidDate(value);
  if (!d) return empty;
  return `${pad2(d.getDate())}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
}

/**
 * Payout cutoff calendar day. Uses the YYYY-MM-DD from the server, not the
 * UTC clock, so 10-SEP 23:59 UTC does not display as 11-SEP in India.
 */
export function formatPayoutCutoffDate(value, empty = '—') {
  if (value == null || value === '') return empty;
  const match = String(value).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return formatAppDate(`${match[1]}-${match[2]}-${match[3]}`, empty);
  }
  return formatAppDate(value, empty);
}

/** Date + time → 07-AUG-2026, 04:01 PM */
export function formatAppDateTime(value, empty = '—') {
  const d = toValidDate(value);
  if (!d) return empty;
  let h = d.getHours();
  const m = pad2(d.getMinutes());
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${formatAppDate(d, empty)}, ${pad2(h)}:${m} ${ampm}`;
}

/**
 * Calendar month label as first-of-month date (payout / EMI month buckets).
 * 2026-07-01 → 01-JUL-2026
 */
export function formatAppMonth(value, empty = '—') {
  const d = toValidDate(value);
  if (!d) return empty;
  return `01-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
}

/** Year-month → 2026-August */
export function formatYearMonthLong(value, empty = '—') {
  if (value == null || value === '') return empty;
  if (typeof value === 'string') {
    const ym = value.trim().match(/^(\d{4})-(\d{2})$/);
    if (ym) {
      const monthIdx = Number(ym[2]) - 1;
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${ym[1]}-${MONTHS_LONG[monthIdx]}`;
      }
    }
  }
  const d = toValidDate(value);
  if (!d) return empty;
  return `${d.getFullYear()}-${MONTHS_LONG[d.getMonth()]}`;
}

/** Rewrite YYYY-MM tokens in free text (e.g. timeline notes) to YYYY-MonthName. */
export function formatYearMonthTokensInText(text) {
  if (text == null || text === '') return text;
  return String(text).replace(/\b(\d{4})-(\d{2})\b/g, (match, y, m) => {
    const monthIdx = Number(m) - 1;
    if (monthIdx < 0 || monthIdx > 11) return match;
    return `${y}-${MONTHS_LONG[monthIdx]}`;
  });
}
